// Import CSS
import './AssessmentFormSwitch.scss'

// React and Third Party Libraries
import { useState, useCallback } from 'react'
import { Form } from 'react-bootstrap'

/**
 * Assessment Form Switch component: used to render a switch
 * @param {Object} initProps - initial props
 * @param {Object} initProps.questionData - question data to be used to build the questions. Must follow the assessment schema
 * @param {String} initProps.questionData.type - type for the option (radio or checkbox)
 * @param {String} initProps.questionData.label - label for the question
 * @param {Number} initProps.questionIndex - index of the question
 * @param {String} initProps.value - value of the input
 * @param {Array} initProps.values - values of the input (for multiple choice)
 * @param {Function} initProps.handleInputChange - function to handle input change
 * @param {String} initProps.handleInputChange.value - value of the input
 * @param {Array} initProps.handleInputChange.values - values of the input (for multiple choice)
 * @param {String} initProps.handleInputChange.name - name of the input
 * @returns {JSX.Element} - Assessment Form Screen Field Group component
 */
export default function AssessmentFormSwitch({
  questionData,
  questionIndex,
  handleInputChange = () => ({ value: '', values: [], name: '' })
}) {
  // State
  const [questionValue, setQuestionValue] = useState('off')

  // Get the question name, type, and options
  const questionName = questionData.name || ''

  /**
   * Change handler for the input. Passes the value of the input
   * to the parent component via the handleInputChange function
   * @param {Object} event - event object
   */
  const onChange = useCallback(
    event => {
      // If there is no event or no target, return
      if (!event?.target) return
      try {
        const value = event.target.checked ? 'on' : 'off'
        setQuestionValue(value)
        handleInputChange({ value, name: questionName })
      } catch (error) {
        console.error(error)
      }
    },
    [handleInputChange, questionName]
  )

  // If we don't have screen data, return null
  if (!questionData) return null

  return (
    <div className='assessment-form-switch' key={questionIndex}>
      {questionData.summary && (
        <p className='assessment-form-switch-summary muted'>
          {questionData.summary}
        </p>
      )}
      <Form.Group className='form-group visible'>
        <Form.Check
          id={questionName}
          className='text-left'
          size='lg'
          label={questionData.label}
          type='switch'
          onChange={onChange}
          checked={questionValue === 'on'}
        />
      </Form.Group>
    </div>
  )
}
